import { ToggleButton, ToggleButtonGroup, Button, IconButton, Menu, MenuItem, Dialog, DialogContent, Slide } from "@mui/material";
import { useState, useEffect, forwardRef } from 'react';
import axios from "axios";
import { fetchAuthSession } from '@aws-amplify/auth';
import LogoutIcon from '@mui/icons-material/Logout';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LoadingMessage from "../../components/LoadingMessage";
import Header from "../../components/general/Header.tsx";
import ErrorMessage from "../../components/ErrorMessage.js";
import PromoCodeInput from "./PromoCodeInput.js";
import CoachUnlinkDialog from "./UnlinkCoaches.js";
import Achievements from "./Achievements.js";
import UserLevel from "../../components/UserLevel.js";

// Slide-up transition for the achievements dialog
const SlideUp = forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Settings = ({userId, signOut}) => {

    const [hasChanged, setHasChanged] = useState(false);
    const [isTokenReady, setIsTokenReady] = useState(false);
    const [settings, setSettings] = useState(null);
    const [coaches, setCoaches] = useState([]);
    const [isError, setIsError] = useState(false);
    const [enteringPromoCode, setEnteringPromoCode] = useState(false);
    const [unlinkingCoaches, setUnlinkingCoaches] = useState(false);
    const [viewingAchievements, setViewingAchievements] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [userLevel, setUserLevel] = useState(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
      const fetchJwtToken = async () => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens.idToken;
          axios.defaults.headers['Authorization'] = `Bearer ${token}`;
          setIsTokenReady(true); 
        } catch (error) {
          console.error('Error fetching JWT token:', error);
          setIsError(true);
        }
      };
  
      fetchJwtToken();
    }, []);
  
    useEffect(() => {
      if (isTokenReady) {
        axios.get(process.env.REACT_APP_API_HOST + `/api/user/${userId}`)
        .then(response => {
          const data = response.data;
          if (data !== null) {
            setSettings(data);
          }
        })
        .catch(error => {
          console.error('Error fetching user settings:', error);
          setIsError(true);
        })

        axios.get(process.env.REACT_APP_API_HOST + `/api/linked-coaches/${userId}`)
        .then(response => {
          const data = response.data;
          if (data !== null) {
            setCoaches(data);
          }
        })
        .catch(error => {
          console.error('Error fetching coach requests:', error);
          setIsError(true);
        })

        axios.get(`${process.env.REACT_APP_API_HOST}/api/achievements/level/${userId}`)
        .then(response => {
          if (response.data !== null) setUserLevel(response.data);
        })
        .catch(error => console.error('Error fetching user level:', error));
      }
    }, [userId, isTokenReady]);

    const handleGenderChange = (event) => {
      setHasChanged(true);
      setSettings({
        ...settings,
        gender: event.target.value
      });
    };

    const handleSubmit = () => {
      axios.patch(process.env.REACT_APP_API_HOST + '/api/user', settings, {
        headers: {
            'Content-Type': 'application/json',
        },
        })
        .catch(error => {
            console.error('Error patching user details:', error);
            setIsError(true);
        }); 
    };

    const refreshLinkedCoaches = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/linked-coaches/${userId}`);
        setCoaches(response.data);
      } catch (err) {
        console.log(err);
      }
    };

    const handleSignout = () => {
      localStorage.removeItem('jwtToken');
      axios.defaults.headers['Authorization'] = '';
      signOut();
    };  

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    const handlePromoCode = () => {
      setAnchorEl(null);
      setEnteringPromoCode(true);
    }

    const manageSubscription = () => {
      axios.post(process.env.REACT_APP_API_HOST + `/payments/portal-session`)
      .then(response => {
        const data = response.data;
        if (data !== null) {
          window.location.href = response.data.url;
        }
      })
      .catch(error => {
        console.error('Error fetching user settings:', error);
        setIsError(true);
      })
    }

    if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;

    if (settings === null) {
      return (<>
        <LoadingMessage title={"Profile"} />
        <IconButton style={{position: 'absolute', top: '35px', right: '0', padding: '0', fontSize: "35px", color: "var(--site-light-text-color)"}} aria-label="delete" size="large" alt='delete' onClick={() => handleSignout()} >
          <LogoutIcon fontSize="inherit" />
        </IconButton>
      </>);
    }

    return (
        <>
          <Header title={'Profile'} />

          <IconButton style={{position: 'absolute', top: '35px', right: '0', padding: '0', fontSize: "35px", color: "var(--site-light-text-color)"}} aria-label="delete" size="large" alt='delete' onClick={() => handleSignout()} >
              <LogoutIcon fontSize="inherit" />
          </IconButton>

          <form className="new-entry" style={{padding: '5px'}}>

            {userLevel && (
              <UserLevel
                level={userLevel.level}
                totalXP={userLevel.total_xp}
                currentLevelXP={userLevel.current_level_xp}
                nextLevelXP={userLevel.next_level_xp}
              />
            )}


            {/* Achievements button */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EmojiEventsIcon />}
              style={{ marginBottom: '10px' }}
              onClick={() => setViewingAchievements(true)}
            >
              Achievements
            </Button>

            <Button style={{width: '49.5%', marginRight: '1%'}} onClick={handleClick}>Manage Subscription</Button>
            <Menu
              id="demo-positioned-menu"
              aria-labelledby="demo-positioned-button"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <MenuItem onClick={manageSubscription}>Update Subscription Details</MenuItem>
              <MenuItem onClick={handlePromoCode}>Apply Promo Code</MenuItem>
            </Menu>

            <Button style={{width: '49.5%'}} onClick={() => setUnlinkingCoaches(true)}>Manage Coaches</Button>

            <h4 style={{marginTop: "10px", textAlign: 'center', fontSize: '1.6rem'}}>What gender do you compete in?</h4>
            <ToggleButtonGroup
              fullWidth
              style={{margin: '10px 0 5px'}}
              color="primary"
              value={settings.gender}
              exclusive
              onChange={(e) => handleGenderChange(e)}
              aria-label="Active status"
            >
              <ToggleButton color="success" value={'male'} aria-label="male">Male</ToggleButton>
              <ToggleButton color="error" value={'female'} aria-label="female">Female</ToggleButton>
            </ToggleButtonGroup>

            <div className='bottom-buttons'>
              <Button
                style={{margin: "10px 5px 10px", width: '100px'}}
                disabled={JSON.parse(settings.disciplines).length === 0 || !hasChanged}
                onClick={handleSubmit}
                variant="contained"
              >
                Save
              </Button>
            </div>

          </form>

          {/* Achievements full-screen dialog */}
          <Dialog
            fullScreen
            open={viewingAchievements}
            onClose={() => setViewingAchievements(false)}
            TransitionComponent={SlideUp}
          >
            <DialogContent style={{ padding: 0 }}>
              {/* Back button */}
              <Button
                onClick={() => setViewingAchievements(false)}
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '8px',
                  zIndex: 10,
                  minWidth: 'auto',
                  padding: '4px 10px',
                  fontSize: '0.8rem',
                }}
              >
                ← Back
              </Button>
              <Achievements userId={userId} />
            </DialogContent>
          </Dialog>

          {unlinkingCoaches && <CoachUnlinkDialog userId={userId} linkedCoaches={coaches} onClose={() => setUnlinkingCoaches(false)} onRefresh={refreshLinkedCoaches} />}
          {enteringPromoCode && <PromoCodeInput handleCancel={() => setEnteringPromoCode(false)} />}
        </>
    );

};

export default Settings;